import React from "react";
import { motion } from "framer-motion";
import { FaUserGraduate, FaLightbulb } from "react-icons/fa";
import { MdOutlineSchool } from "react-icons/md";
import onlineTutorials from "../../assets/hero-banner.png";

// Animation variants
const fadeInUp = {
  initial: { opacity: 0, y: 20 },
  animate: { opacity: 1, y: 0 },
  transition: { duration: 0.5 }
};

const floatingAnimation = {
  animate: {
    y: [0, -15, 0],
    x: [0, -5, 0],
    rotate: [0, -5, 0]
  },
  transition: {
    duration: 5,
    repeat: Infinity,
    ease: "easeInOut"
  }
};

// Reusable components
const InfoCard = ({ icon: Icon, title, subtitle, position }) => (
  <div className={`absolute ${position} bg-white/80 backdrop-blur-md rounded-xl sm:rounded-2xl p-2 sm:p-2.5 border border-white/20 shadow-lg transform scale-90 sm:scale-100 hidden sm:block`}>
    <div className="flex items-center space-x-2">
      <div className="p-1 sm:p-1.5 bg-gradient-to-br from-[rgb(4,35,107)] to-[rgb(4,35,107)]/80 rounded-lg text-white">
        <Icon className="text-sm sm:text-base" />
      </div>
      <div>
        <p className="font-semibold text-[rgb(4,35,107)] text-[10px] sm:text-xs">{title}</p>
        <p className="text-gray-500 text-[8px] sm:text-[10px]">{subtitle}</p>
      </div>
    </div>
  </div>
);


const StatBadge = ({ icon: Icon, value, label }) => (
  <motion.div
    whileHover={{ scale: 1.05 }}
    className="relative group w-full sm:w-auto"
  >
    <div className="absolute -inset-1 bg-gradient-to-r from-[rgb(4,35,107)] to-[#f6b51b] rounded-lg blur opacity-25 group-hover:opacity-40 transition duration-200" />
    <div className="relative flex items-center space-x-2 sm:space-x-3 bg-white px-3 sm:px-4 py-2 sm:py-3 rounded-lg shadow-sm">
      <div className="p-1 sm:p-1.5 bg-gradient-to-br from-[rgb(4,35,107)]/20 to-[rgb(4,35,107)]/10 rounded-lg">
        <Icon className="text-base sm:text-lg text-[rgb(4,35,107)]" />
      </div>
      <div>
        <p className="font-bold text-[rgb(4,35,107)] text-sm sm:text-base">{value}</p>
        <p className="text-[10px] sm:text-xs text-gray-500">{label}</p>
      </div>
    </div>
  </motion.div>
);

const Hero = () => {
  return (
    <section className="relative bg-white overflow-hidden">
      <div className="max-w-7xl mx-auto">
        <div className="min-h-[80vh] py-8 sm:py-12 md:py-14 px-4 md:px-8 relative">
          {/* Background Decorative Elements */}
          <div className="absolute top-0 left-0 w-full h-full overflow-hidden pointer-events-none">
            <motion.div
              animate={{ 
                scale: [1, 1.2, 1],
                rotate: [0, 5, 0]
              }}
              transition={{ duration: 8, repeat: Infinity }}
              className="absolute -top-20 -right-20 w-48 sm:w-64 h-48 sm:h-64 bg-[rgb(4,35,107)]/5 rounded-full blur-3xl"
            />
            <motion.div
              animate={{ 
                scale: [1, 1.3, 1],
                rotate: [0, -5, 0]
              }}
              transition={{ duration: 10, repeat: Infinity }}
              className="absolute -bottom-20 -left-20 w-56 sm:w-72 h-56 sm:h-72 bg-[#f6b51b]/10 rounded-full blur-3xl"
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 sm:gap-12 items-center">
            {/* Left Content */}
            <motion.div
              initial={{ opacity: 0, x: -50 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
              className="space-y-6 sm:space-y-8"
            >
              <div className="space-y-4 sm:space-y-6">
                <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold leading-tight">
                  <span className="text-[rgb(4,35,107)]">Expert Guidance</span>
                  <br />
                  <span className="bg-gradient-to-r from-[rgb(4,35,107)] to-[#f6b51b] bg-clip-text text-transparent">
                    For Your Future
                  </span>
                </h1>
                <p className="text-gray-600 text-sm sm:text-base md:text-lg leading-relaxed max-w-xl">
                  We are building a one-of-a-kind nurturing ecosystem for learners using technology and human efforts.
                </p>
              </div>

              {/* CTA Section */}
              <div className="flex flex-col sm:flex-row items-center gap-4 sm:gap-6">
                <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  className="w-full sm:w-auto px-4 sm:px-6 py-2.5 sm:py-3 bg-white text-[rgb(4,35,107)] rounded-full font-semibold flex items-center justify-center space-x-2 border-2 border-[rgb(4,35,107)] hover:bg-[rgb(4,35,107)] hover:text-white transition-colors duration-300 text-sm sm:text-base"
                >
                  <span>Get Expert Guidance</span>
                  <svg className="w-4 h-4 sm:w-5 sm:h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
                  </svg>
                </motion.button>

                {/* Play Button Section */}
                <div className="flex items-center space-x-2 sm:space-x-3">
                  <div className="relative">
                    {/* Dotted Circle */}
                    <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-full border-2 border-dotted border-[rgb(4,35,107)] flex items-center justify-center">
                      {/* Play Button Background */}
                      <div className="w-8 h-8 sm:w-10 sm:h-10 rounded-full bg-[rgb(4,35,107)]/10 flex items-center justify-center">
                        {/* Play Icon */}
                        <div className="w-6 h-6 sm:w-8 sm:h-8 rounded-full bg-[rgb(4,35,107)] flex items-center justify-center">
                          <svg className="w-3 h-3 sm:w-4 sm:h-4 text-white" fill="currentColor" viewBox="0 0 24 24">
                            <path d="M8 5v14l11-7z" />
                          </svg>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div>
                    <span className="font-medium text-[rgb(4,35,107)] block text-xs sm:text-sm">See how it works</span>
                  </div>
                </div>
              </div>

              {/* Stats Section */}
              <div className="grid grid-cols-2 sm:flex sm:flex-wrap items-center gap-3 sm:gap-4">
                <StatBadge
                  icon={FaUserGraduate}
                  value="100%"
                  label="Success Rate"
                />
                <StatBadge
                  icon={MdOutlineSchool}
                  value="500+"
                  label="Success Stories"
                />
              </div>
            </motion.div>

            {/* Right Image Section */}
            <motion.div
              initial={{ opacity: 0, x: 50 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8, delay: 0.2 }}
              className="relative mt-8 md:mt-0"
            >
              <div className="relative">
                <motion.img
                  initial={{ scale: 0.9 }}
                  animate={{ scale: 1 }}
                  transition={{ duration: 0.5 }}
                  src={onlineTutorials}
                  alt="Online Learning"
                  className="w-full h-auto rounded-lg sm:rounded-none"
                />

                {/* Info Cards */}
                <InfoCard
                  icon={FaUserGraduate}
                  title="Expert Mentors"
                  subtitle="Personalized"
                  position="-top-2 sm:-top-4 left-1/4"
                />

                <InfoCard
                  icon={MdOutlineSchool}
                  title="College Support"
                  subtitle="Admission Help"
                  position="-top-2 sm:-top-4 -right-2 sm:-right-4"
                />

                {/* Bottom Element */}
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: 0.6 }}
                  className="absolute -bottom-4 sm:-bottom-6 left-1/3 transform -translate-x-1/2 scale-90 sm:scale-100"
                >
                  <div className="relative">
                    <div className="absolute inset-0 bg-gradient-to-r from-[rgb(4,35,107)]/20 to-[#f6b51b]/20 blur-xl rounded-full" />
                    <div className="relative bg-white/90 backdrop-blur-md rounded-xl sm:rounded-2xl p-2.5 sm:p-3 border border-white/20">
                      <div className="flex items-center space-x-2 sm:space-x-3">
                        <div className="relative">
                          <div className="absolute inset-0 bg-gradient-to-r from-[rgb(4,35,107)] to-[#f6b51b] rounded-lg blur-sm" />
                          <div className="relative p-1.5 sm:p-2 bg-gradient-to-br from-[rgb(4,35,107)] to-[#f6b51b] rounded-lg text-white">
                            <FaLightbulb className="text-base sm:text-lg" />
                          </div>
                        </div>
                        <div>
                          <p className="font-semibold text-[rgb(4,35,107)] text-xs sm:text-sm">Smart Path</p>
                          <p className="text-gray-500 text-[10px] sm:text-xs">Career Guidance</p>
                        </div>
                      </div>
                    </div>
                  </div>
                </motion.div>
              </div>
            </motion.div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Hero;
